import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Volume2,
  ChevronLeft,
  ChevronDown,
  CheckCircle,
  RotateCw,
  ChevronRight,
  ArrowLeft,
  Check,
  Lightbulb,
  PenLine,
} from 'lucide-react';

import { useTheme } from './ThemeContext';
import './WordListPreview.css';

import FloatingBackButton from './FloatingBackButton';

import { WordData,UserProgress,  } from '../types/types';
import { getWordTranslation, getWordExplanation, getWordExample } from '../utils/localizedContent';
import { displayCategoryLabel } from '../utils/categoryLabels';

interface WordListPreviewProps {
  title: string;
  words: [string, WordData][];
  onBack: () => void;
  onStartLearning: () => void;
  onStartRevision: () => void;
  completedWordsCount: number;
  playAudio: (audioSrc: string) => Promise<void>;
  completedWords: Set<string>;
  isMobileView?: boolean;
  languageCode: string;
  userProgress?: UserProgress; 
}

type SortOrder = 'default' | 'alphabetical' | 'learned' | 'progress';



const WordListPreview: React.FC<WordListPreviewProps> = ({ 
  title, 
  words, 
  onBack, 
  onStartLearning,
  onStartRevision,

  playAudio,

  isMobileView = false,
  languageCode,
  userProgress
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  // États pour forcer le rechargement des mots appris
  const [learnedWordsRefreshTrigger, setLearnedWordsRefreshTrigger] = useState(0);
  
  // CLÉ STABLE qui inclut maintenant le trigger de rafraîchissement
  const stableComponentKey = useMemo(() => {
    return `wordlist-${title}-${words.length}-${learnedWordsRefreshTrigger}`;
  }, [title, words.length, learnedWordsRefreshTrigger]);
  
  // États avec clés uniques pour éviter les conflits
  const [expandedWord, setExpandedWord] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('default');
  
  const [audioLoading, setAudioLoading] = useState<string | null>(null);
  const [isChangingPage, setIsChangingPage] = useState(false);
  
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Configuration
  const itemsPerPage = 5;
  
  // Fonction de nettoyage des mots (supprime les parenthèses)
  const cleanWord = useCallback((word: string): string => {
    return word.split('(')[0].trim();
  }, []);
  
  // Fonction pour charger les mots appris depuis localStorage
 const loadLearnedWordsFromStorage = useCallback((): Set<string> => {
  const learnedWords = new Set<string>();
  
  
  
  try {
    // ✅ PRIORITÉ 1: userProgress.recentlyLearnedWords (SOURCE PRINCIPALE)
    if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
    
      
      userProgress.recentlyLearnedWords.forEach(item => {
        // Vérifier si le mot appartient à la catégorie (title)
        if (item.category === title) {
          learnedWords.add(item.word);
          learnedWords.add(cleanWord(item.word));
       
        }
      });
      
     
    } else {
}
    
    // 2. Charger les mots de la catégorie spécifique (fallback)
    const categoryLearnedKey = `${languageCode}-${title}-learnedWords`;
    const savedCategoryLearned = localStorage.getItem(categoryLearnedKey);
    
    if (savedCategoryLearned) {
      const categoryWords = JSON.parse(savedCategoryLearned) as string[];
      categoryWords.forEach(word => {
        learnedWords.add(word);
        learnedWords.add(cleanWord(word));
      });
   
    }
    
    // 3. Charger les mots de la session locale (fallback)
    const sessionLearnedKey = `${languageCode}-${title}-sessionLearned`;
    const savedSessionLearned = localStorage.getItem(sessionLearnedKey);
    
    if (savedSessionLearned) {
      const sessionWords = JSON.parse(savedSessionLearned) as string[];
      sessionWords.forEach(word => {
        learnedWords.add(word);
        learnedWords.add(cleanWord(word));
      });
    
    }
    
    // 4. Fallback: liste globale (pour compatibilité)
    const globalLearnedKey = `${languageCode}-allLearnedWords`;
    const savedGlobalLearned = localStorage.getItem(globalLearnedKey);
    
    if (savedGlobalLearned) {
      const globalWords = JSON.parse(savedGlobalLearned) as string[];
      globalWords.forEach(word => {
        learnedWords.add(word);
        learnedWords.add(cleanWord(word));
      });
     
    }
    
   
    
  } catch (error) {
}
  
  return learnedWords;
}, [languageCode, title, cleanWord, userProgress]);
  
  // État local pour les mots appris avec rechargement automatique
  const [localLearnedWords, setLocalLearnedWords] = useState<Set<string>>(() => 
    loadLearnedWordsFromStorage()
  );
  
  // SURVEILLANCE: Surveiller les changements dans localStorage
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Vérifier si le changement concerne notre langue et catégorie
      if (event.key && (
        event.key.includes(`${languageCode}-${title}-`) ||
        event.key.includes(`${languageCode}-allLearnedWords`)
      )) {
     
        
        // Recharger les mots appris
        const newLearnedWords = loadLearnedWordsFromStorage();
        setLocalLearnedWords(newLearnedWords);
        
        // Forcer le rafraîchissement du composant
        setLearnedWordsRefreshTrigger(prev => prev + 1);
      }
    };

    // Écouter les événements de changement de localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // SURVEILLANCE SUPPLÉMENTAIRE: Polling périodique pour les changements internes
    const pollInterval = setInterval(() => {
      const currentLearnedWords = loadLearnedWordsFromStorage();
      
      // Comparer avec l'état actuel
      if (currentLearnedWords.size !== localLearnedWords.size) {
       
        setLocalLearnedWords(currentLearnedWords);
        setLearnedWordsRefreshTrigger(prev => prev + 1);
      }
    }, 2000); // Vérifier toutes les 2 secondes
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [languageCode, title, loadLearnedWordsFromStorage, localLearnedWords.size]);
  
  // RÉINITIALISER UNIQUEMENT quand la catégorie change vraiment
  useEffect(() => {

    setExpandedWord(null);
    setCurrentPage(0);
    setSearchQuery('');
    setSortOrder('default');
    setAudioLoading(null);
    setIsChangingPage(false);
    
    // RECHARGEMENT: Recharger les mots appris au changement de catégorie
    const newLearnedWords = loadLearnedWordsFromStorage();
    setLocalLearnedWords(newLearnedWords);
    setLearnedWordsRefreshTrigger(prev => prev + 1);
  }, [title, languageCode, loadLearnedWordsFromStorage]);
  
  // Vérification mise à jour avec l'état local
  const isWordLearned = useCallback((word: string): boolean => {
    const baseWord = cleanWord(word);
    const variations = [
      word,
      baseWord,
      word.toLowerCase(),
      baseWord.toLowerCase()
    ];
    
    return variations.some(variant => localLearnedWords.has(variant));
  }, [localLearnedWords, cleanWord]);
  
  // RECALCUL: Calcul dynamique du nombre de mots complétés
  const recalculatedCompletedWordsCount = useMemo(() => {
    return words.reduce((count: number, [word]: [string, WordData]) => {
      return isWordLearned(word) ? count + 1 : count;
    }, 0);
  }, [words, isWordLearned]);
  
  // Utiliser le count recalculé au lieu de celui des props
  const effectiveCompletedWordsCount = recalculatedCompletedWordsCount;
  const isCategoryCompleted = effectiveCompletedWordsCount === words.length;
  
  // Filtrage et tri des mots (memoized pour performance)
  const processedWords = useMemo(() => {
    // Filtrage
    let filtered = words.filter(([word, data]) => {
      if (searchQuery === '') return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        word.toLowerCase().includes(searchLower) || 
        data.translation.toLowerCase().includes(searchLower) ||
        data.explanation?.toLowerCase().includes(searchLower) ||
        data.example?.toLowerCase().includes(searchLower)
      );
    });
    
    // Tri
    const sorted = [...filtered].sort((a, b) => {
      const [wordA, dataA] = a;
      const [wordB, dataB] = b;
      
      switch (sortOrder) {
        case 'alphabetical':
          return cleanWord(wordA).localeCompare(cleanWord(wordB));
        
        case 'learned':
          const aLearned = isWordLearned(wordA) ? 1 : 0;
          const bLearned = isWordLearned(wordB) ? 1 : 0;
          return bLearned - aLearned;
        
        case 'progress':
          // Tri par progression (mots avec explications/exemples en premier)
          const aHasDetails = (dataA.explanation || dataA.example) ? 1 : 0;
          const bHasDetails = (dataB.explanation || dataB.example) ? 1 : 0;
          return bHasDetails - aHasDetails;
        
        default:
          return 0; // Garde l'ordre original
      }
    });
    
    return sorted;
  }, [words, searchQuery, sortOrder, isWordLearned, cleanWord]);
  
  // Pagination
  const totalPages = Math.ceil(processedWords.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const visibleWords = processedWords.slice(startIndex, startIndex + itemsPerPage);
  
  // Calcul du pourcentage de progression
  const progressPercentage = words.length > 0 ? Math.round((effectiveCompletedWordsCount / words.length) * 100) : 0;
  
  // GESTION DE PAGINATION CORRIGÉE - Évite le déploiement infini
  const changePage = useCallback((newPage: number) => {
    
    
   
    
    
    // Marquer qu'un changement est en cours
    setIsChangingPage(true);
    
    // Fermer les détails lors du changement de page
    setExpandedWord(null);
    
    // Changer la page après un court délai pour éviter les conflits
    setTimeout(() => {
      setCurrentPage(newPage);
      
      // Scroll doux vers le haut
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      
      // Débloquer après le changement de page
      setTimeout(() => {
        setIsChangingPage(false);
      }, 300);
    }, 50);
    
  }, [totalPages, currentPage, isChangingPage]);
  
  // Gestion des gestes tactiles
 
  
 
  
  // Gestion de l'audio avec état de chargement
  const handleAudioPlay = useCallback(async (audioSrc: string, word: string) => {
    try {
      setAudioLoading(word);
      await playAudio(audioSrc);
    } catch (error) {
} finally {
      setAudioLoading(null);
    }
  }, [playAudio]);
  
  // Gestion de l'expansion des détails
  const toggleWordDetails = useCallback((word: string) => {
    if (isChangingPage) return;
    setExpandedWord(prev => prev === word ? null : word);
  }, [isChangingPage]);
  
  // Réinitialiser la page lors du changement de filtre
  useEffect(() => {
    setCurrentPage(0);
    setExpandedWord(null);
    setIsChangingPage(false);
  }, [searchQuery, sortOrder]);
  
  // VARIANTS D'ANIMATION OPTIMISÉS pour éviter les conflits
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.02,
        duration: 0.15
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.1,
        staggerChildren: 0.01,
        staggerDirection: -1
      }
    }
  };
  
  const itemVariants = {
    hidden: { 
      y: 8,
      opacity: 0 
    },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "tween",
        duration: 0.15,
        ease: "easeOut"
      }
    },
    exit: {
      y: -8,
      opacity: 0,
      transition: {
        duration: 0.1
      }
    }
  };
  
  const detailsVariants = {
    hidden: { 
      height: 0, 
      opacity: 0,
      paddingTop: 0,
      paddingBottom: 0 
    },
    visible: { 
      height: "auto", 
      opacity: 1,
      paddingTop: "var(--spacing-lg)",
      paddingBottom: "var(--spacing-lg)",
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    exit: {
      height: 0,
      opacity: 0,
      paddingTop: 0,
      paddingBottom: 0,
      transition: {
        duration: 0.15
      }
    }
  };
  
  // Génération des numéros de page à afficher
  const getPageNumbers = useCallback(() => {
    const maxVisible = 5;
    const pages: number[] = [];
    
    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage < 3) {
        for (let i = 0; i < maxVisible; i++) {
          pages.push(i);
        }
      } else if (currentPage > totalPages - 3) {
        for (let i = totalPages - maxVisible; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  // Gestion du retour avec animation de sortie
  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  // CLÉ UNIQUE POUR LA GRILLE DE MOTS basée sur la page actuelle
  const wordsGridKey = useMemo(() => {
    return `words-grid-${currentPage}-${processedWords.length}-${learnedWordsRefreshTrigger}`;
  }, [currentPage, processedWords.length, learnedWordsRefreshTrigger]);


  // ── Mastery dots helper (0-6) ─────────────────────────────────────────────
  const getMasteryLevel = useCallback((word: string): number => {
    const progress = userProgress?.wordProgress;
    if (!progress) return isWordLearned(word) ? 2 : 0;
    const wp = progress[word] ?? progress[cleanWord(word)];
    if (!wp) return isWordLearned(word) ? 2 : 0;
    const { correct, interval } = wp;
    if (interval >= 30) return 6;
    if (interval >= 14) return 5;
    if (interval >= 7)  return 4;
    if (interval >= 3)  return 3;
    if (correct >= 1)   return 2;
    return 1;
  }, [userProgress, isWordLearned, cleanWord]);

  const renderContent = () => (
    <div className="wlp-screen" data-theme={theme}>
      {/* Mobile : bouton flottant via portal */}
      {isMobileView && <FloatingBackButton onBack={handleBack} position="top-left" variant="minimal" />}

      {/* Desktop : bouton dans le flux */}
      {!isMobileView && (
        <motion.button
          className="wlp-desktop-back-btn"
          onClick={handleBack}
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft size={18} /> Retour
        </motion.button>
      )}

      {/* ── En-tête catégorie ── */}
      <div className="wlp-topbar">
        <p className="wlp-topbar__sub">{t('wordListPreview.vocabulary')}</p>
        <h1 className="wlp-topbar__title">{displayCategoryLabel(title, t)}</h1>
        <div className="wlp-progress-bar">
          <motion.div
            className="wlp-progress-bar__fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
        <p className="wlp-topbar__count">
          {t('wordListPreview.wordsLearnedCount', { done: effectiveCompletedWordsCount, total: words.length })}
        </p>
      </div>

      {/* ── Bouton principal ── */}
      <div className="wlp-actions">
        {isCategoryCompleted ? (
          <motion.button
            className="wlp-btn wlp-btn--accent"
            onClick={onStartRevision}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('wordListPreview.reviewCategory')}
          </motion.button>
        ) : (
          <motion.button
            className="wlp-btn wlp-btn--accent"
            onClick={onStartLearning}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('sentenceConstruction.startLearningCount', { count: words.length - effectiveCompletedWordsCount })}
          </motion.button>
        )}
        {effectiveCompletedWordsCount > 0 && !isCategoryCompleted && (
          <motion.button
            className="wlp-btn wlp-btn--ghost"
            onClick={onStartRevision}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('sentenceConstruction.review', { count: effectiveCompletedWordsCount })}
          </motion.button>
        )}
      </div>

      {/* ── Liste des mots ── */}
      <div className="wlp-section">
        <div className="wlp-section-header">
          <span className="wlp-section-title">{t('grammar.wordsOfCategory')}</span>
        </div>

        {/* Cadre décoratif haut */}
        <div className="wlp-deco-border" aria-hidden="true" />

        <motion.div
          key={wordsGridKey}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {visibleWords.map(([word, data], index) => {
            const isLearned    = isWordLearned(word);
            const mastery      = getMasteryLevel(word);
            const globalIndex  = startIndex + index + 1;
            const hasDetails   = Boolean(data.explanation || data.example);
            const isExpanded   = expandedWord === word;

            return (
              <motion.div
                key={`${word}-${globalIndex}-${learnedWordsRefreshTrigger}`}
                className={`wlp-row${isLearned ? ' wlp-row--learned' : ''}`}
                variants={itemVariants}
              >
                <div
                  className="wlp-row__main"
                  onClick={() => hasDetails && toggleWordDetails(word)}
                  style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                >
                  {/* Mot + traduction */}
                  <div className="wlp-row__text">
                    <span className="wlp-row__word">{cleanWord(word)}</span>
                    <span className="wlp-row__translation">{getWordTranslation(data, i18n.language)}</span>
                  </div>

                  {/* Dots + audio */}
                  <div className="wlp-row__right">
                    {isLearned ? (
                      <div className="wlp-learned-badge" aria-label={t('wordListPreview.wordLearnedAria') as string}>
                        <Check className="wlp-learned-badge__check" size={15} strokeWidth={3} />
                      </div>
                    ) : null}

                    {data.audio && (
                      <motion.button
                        className={`wlp-audio-btn${audioLoading === word ? ' loading' : ''}`}
                        onClick={e => { e.stopPropagation(); handleAudioPlay(data.audio!, word); }}
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.9 }}
                        disabled={audioLoading === word}
                        aria-label={t('learnMode.listenButton') as string}
                      >
                        {audioLoading === word
                          ? <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>⟳</motion.span>
                          : <Volume2 size={16} />
                        }
                      </motion.button>
                    )}

                    {hasDetails && (
                      <motion.button
                        className="wlp-expand-btn"
                        onClick={e => { e.stopPropagation(); toggleWordDetails(word); }}
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        aria-label={t('wordListPreview.detailsAria') as string}
                      >
                        <ChevronDown size={15} />
                      </motion.button>
                    )}
                  </div>
                </div>

                {/* Détails (explication + exemple) */}
                <AnimatePresence>
                  {isExpanded && hasDetails && (
                    <motion.div
                      className="wlp-row__details"
                      key={`details-${word}`}
                      variants={detailsVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {data.explanation && (
                        <p className="wlp-detail">
                          <span className="wlp-detail__label"><Lightbulb size={14} strokeWidth={1.75} /></span>
                          {getWordExplanation(data, i18n.language)}
                        </p>
                      )}
                      {data.example && (
                        <p className="wlp-detail">
                          <span className="wlp-detail__label"><PenLine size={14} strokeWidth={1.75} /></span>
                          {getWordExample(data, i18n.language)}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Cadre décoratif bas */}
        <div className="wlp-deco-border wlp-deco-border--bottom" aria-hidden="true" />
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="wlp-pagination">
          <motion.button
            className="wlp-page-btn"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 0 || isChangingPage}
            whileHover={currentPage !== 0 ? { scale: 1.1 } : {}}
          >
            <ChevronLeft size={18} />
          </motion.button>

          {getPageNumbers().map(p => (
            <motion.button
              key={p}
              className={`wlp-page-num${p === currentPage ? ' active' : ''}`}
              onClick={() => changePage(p)}
              disabled={isChangingPage}
              whileHover={p !== currentPage ? { y: -2 } : {}}
            >
              {p + 1}
            </motion.button>
          ))}

          <motion.button
            className="wlp-page-btn"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages - 1 || isChangingPage}
            whileHover={currentPage !== totalPages - 1 ? { scale: 1.1 } : {}}
          >
            <ChevronRight size={18} />
          </motion.button>
        </div>
      )}
    </div>
  );

  // RENDU PRINCIPAL STABLE
  return (
    <div className="word-list-preview-container" data-theme={theme}>
      <motion.div
        key={stableComponentKey}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.25 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
};

export default WordListPreview;
